/* ===========================================================
   editor/nodes/math.js — equations (inline and on their own line)

   Stored as LaTeX in an attribute. Clicking one opens the equation
   editor (with a live preview). In Developer mode you can also type
   them straight in: $x^2$ for inline, $$ on an empty line for a
   block. Markdown: $…$ and $$…$$.
   =========================================================== */

import { Node, InputRule } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { env } from '../env.js';
import { renderMath } from '../../lib/katex.js';

function mathView(display) {
  return ({ node, getPos, editor }) => {
    const dom = document.createElement(display ? 'div' : 'span');
    dom.className = display ? 'math-block' : 'math-inline';
    dom.contentEditable = 'false';
    dom.setAttribute('role', 'button');
    dom.setAttribute('aria-label', `Equation: ${node.attrs.latex || 'empty'}. Press Enter to edit.`);
    let current = node;
    renderMath(dom, current.attrs.latex, display);

    const open = e => {
      e?.preventDefault();
      if (!editor.isEditable) return;
      editMath(editor, getPos, current, display);
    };
    dom.addEventListener('click', open);

    return {
      dom,
      update(n) {
        if (n.type !== current.type) return false;
        if (n.attrs.latex !== current.attrs.latex) {
          renderMath(dom, n.attrs.latex, display);
          dom.setAttribute('aria-label', `Equation: ${n.attrs.latex || 'empty'}. Press Enter to edit.`);
        }
        current = n;
        return true;
      },
      selectNode() { dom.classList.add('is-selected'); },
      deselectNode() { dom.classList.remove('is-selected'); },
      ignoreMutation: () => true,
      stopEvent: e => e.type === 'click' || e.type === 'mousedown',
    };
  };
}

/** Open the equation editor for a node; save, switch inline ⇄ own line, or remove. */
export async function editMath(editor, getPos, node, display) {
  const r = await env.editEquation({ latex: node.attrs.latex, display });
  if (!r) return;
  const pos = typeof getPos === 'function' ? getPos() : getPos;
  if (typeof pos !== 'number') return;
  const range = { from: pos, to: pos + node.nodeSize };
  const chain = editor.chain().focus();
  if (!r.latex.trim()) chain.deleteRange(range).run();
  else if (r.display !== display) chain.insertContentAt(range, { type: r.display ? 'mathBlock' : 'mathInline', attrs: { latex: r.latex } }).run();
  else chain.command(({ tr }) => { tr.setNodeMarkup(pos, undefined, { ...node.attrs, latex: r.latex }); return true; }).run();
}

const latexAttr = {
  latex: {
    default: '',
    parseHTML: el => el.getAttribute('data-latex') || '',
    renderHTML: a => ({ 'data-latex': a.latex }),
  },
};

/** Enter on a selected equation opens it. */
function enterOpens(editor, name, display) {
  const { selection } = editor.state;
  if (!(selection instanceof NodeSelection) || selection.node.type.name !== name) return false;
  editMath(editor, selection.from, selection.node, display);
  return true;
}

export const MathInline = Node.create({
  name: 'mathInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes: () => latexAttr,
  parseHTML: () => [{ tag: 'span[data-math-inline]' }],
  renderHTML: ({ HTMLAttributes }) => ['span', { 'data-math-inline': '', ...HTMLAttributes }],
  addNodeView() { return mathView(false); },
  addKeyboardShortcuts() { return { Enter: () => enterOpens(this.editor, this.name, false) }; },
  addInputRules() {
    return [new InputRule({
      // $x^2$  (Developer mode only; a lone $ for money is left alone)
      find: /(?:^|[\s(])\$([^$\s](?:[^$]*[^$\s])?)\$$/,
      handler: ({ state, range, match }) => {
        if (env.mode() !== 'dev') return null;
        const start = range.from + match[0].indexOf('$');
        state.tr.replaceWith(start, range.to, this.type.create({ latex: match[1] }));
      },
    })];
  },
  markdownTokenName: 'mathInline',
  markdownTokenizer: {
    name: 'mathInline',
    level: 'inline',
    start: src => src.indexOf('$'),
    tokenize(src) {
      const m = /^\$(?!\$)((?:\\\$|[^$\n])+?)\$(?!\d)/.exec(src);
      if (!m) return undefined;
      return { type: 'mathInline', raw: m[0], text: m[1] };
    },
  },
  parseMarkdown: (token, h) => h.createNode('mathInline', { latex: token.text }),
  renderMarkdown: node => `$${node.attrs?.latex || ''}$`,
});

export const MathBlock = Node.create({
  name: 'mathBlock',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes: () => latexAttr,
  parseHTML: () => [{ tag: 'div[data-math-block]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', { 'data-math-block': '', ...HTMLAttributes }],
  addNodeView() { return mathView(true); },
  addKeyboardShortcuts() { return { Enter: () => enterOpens(this.editor, this.name, true) }; },
  addInputRules() {
    const editor = this.editor;
    const type = this.type;
    return [new InputRule({
      // "$$ " on an empty line → an equation block (Developer mode)
      find: /^\$\$\s$/,
      handler: ({ state, range }) => {
        if (env.mode() !== 'dev') return null;
        const $from = state.doc.resolve(range.from);
        const start = $from.before();
        const node = type.create({ latex: '' });
        state.tr.replaceWith(start, $from.after(), node);
        setTimeout(() => editMath(editor, start, node, true), 0);
      },
    })];
  },
  markdownTokenName: 'mathBlock',
  markdownTokenizer: {
    name: 'mathBlock',
    level: 'block',
    start: src => src.indexOf('$$'),
    tokenize(src) {
      const m = /^\$\$\s*\n?([\s\S]+?)\n?\s*\$\$[ \t]*(?:\n|$)/.exec(src);
      if (!m) return undefined;
      return { type: 'mathBlock', raw: m[0], text: m[1].trim() };
    },
  },
  parseMarkdown: (token, h) => h.createNode('mathBlock', { latex: token.text }),
  renderMarkdown: node => `$$\n${node.attrs?.latex || ''}\n$$`,
});
